/**
 * Landing Feature - Use Cases
 * Business logic for landing page operations
 */

import type { ILandingRepository } from "../repositories";
import type {
  ContactFormData,
  MailingListSubscription,
  ApiResult,
} from "../entities";

/**
 * Validation helpers
 */
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidName = (name: string): boolean => {
  return name.trim().length >= 2;
};

/**
 * Create landing use cases with dependency injection
 */
export const createLandingUseCases = (repository: ILandingRepository) => ({
  /**
   * Submit contact form with validation
   */
  submitContactForm: async (data: ContactFormData): Promise<ApiResult> => {
    // Validate required fields
    if (!isValidName(data.name)) {
      return {
        success: false,
        message: "landing.contact.validation.nameRequired",
      };
    }

    if (!isValidEmail(data.email)) {
      return {
        success: false,
        message: "landing.contact.validation.emailInvalid",
      };
    }

    return repository.submitContactForm(data);
  },

  /**
   * Subscribe to mailing list with validation
   */
  subscribeToMailingList: async (
    data: MailingListSubscription
  ): Promise<ApiResult> => {
    if (!isValidEmail(data.email)) {
      return {
        success: false,
        message: "landing.subscribe.validation.emailInvalid",
      };
    }

    return repository.subscribeToMailingList(data);
  },

  /**
   * Validate email format
   */
  isEmailValid: (email: string): boolean => isValidEmail(email),

  /**
   * Validate name format
   */
  isNameValid: (name: string): boolean => isValidName(name),
});

export type LandingUseCases = ReturnType<typeof createLandingUseCases>;
